import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { View, Pressable, StyleSheet, Platform, FlatList, TextInput, ActivityIndicator } from 'react-native';
import { useAuthStore, useToastStore, useModelStore, useCompanyStore } from '@/store';
import SettingsSection from '@/components/SettingsSection';
import ModelSelectorDetailedDropdown from '@/components/Dropdowns/ModelSelectorDetailedDropdown';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import usePersonalOnboardingInvitation from '@/hooks/usePersonalOnboardingInvitation';
import PersonalOnboardingInvitation from '@/components/Onboarding/PersonalOnboardingInvitation';

const ModelRow = React.memo(function ModelRow({ model, isSelected, onPress, colors, accentColor }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        nativeStyles.row,
        { borderBottomColor: colors.border_color_secondary },
        isHovered && !isSelected && { backgroundColor: colors.solid_color_secondary },
      ]}
    >
      <View style={[nativeStyles.radio, { borderColor: isSelected ? accentColor : colors.text_color_tertiary }]}>
        {isSelected && <View style={[nativeStyles.radioDot, { backgroundColor: accentColor }]} />}
      </View>
      {model.logoPath ? (
        <Image source={{ uri: model.logoPath }} style={nativeStyles.logo} />
      ) : (
        <View style={[nativeStyles.logoPlaceholder, { backgroundColor: colors.solid_color_tertiary }]}>
          <Icon name="sparkles" size={12} color={colors.text_color_tertiary} />
        </View>
      )}
      <View style={nativeStyles.info}>
        <Text size="s" weight={isSelected ? 'medium' : 'regular'} numberOfLines={1}>{model.name}</Text>
        {model.companyName ? (
          <Text size="xs" color="tertiary" numberOfLines={1}>{model.companyName}</Text>
        ) : null}
      </View>
    </Pressable>
  );
});

const nativeStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 12,
    borderBottomWidth: 0.5,
    minHeight: 52,
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  logo: {
    width: 28,
    height: 28,
    borderRadius: 0,
    backgroundColor: '#fff',
  },
  logoPlaceholder: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  info: {
    flex: 1,
    gap: 2,
  },
});

function isActiveModel(m) {
  return m && m.flags?.isEnabled && !m.flags?.isDeprecated;
}

function PersonalizationSettings({ embeddedInPage = false, embeddedInGeneral = false }) {
  const invitation = usePersonalOnboardingInvitation({ enabled: !embeddedInGeneral });
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const { addToast } = useToastStore(state => state.actions);
  const setDefaultModel = useAuthStore(state => state.setDefaultModel);
  const defaultTextModelId = useAuthStore(state => {
    const raw = state.user?.defaultModel?.text;
    if (!raw) return null;
    return typeof raw === 'string' ? raw : raw.id || null;
  });
  const allModels = useModelStore(state => state.models);
  const allModelIds = useModelStore(state => state.allModelIds);
  const featuredModelIds = useModelStore(state => state.featuredModelIds);
  const fetchAllModels = useModelStore(state => state.fetchAllModels);
  const searchModels = useModelStore(state => state.searchModels);
  const searchResults = useModelStore(state => state.searchResults);
  const isSearching = useModelStore(state => state.isSearching);
  const isFetchedAllModels = useModelStore(state => state.isFetchedAllModels);
  const isLoadingAllModels = useModelStore(state => state.isLoadingAllModels);
  const modelsPagination = useModelStore(state => state.pagination?.allModels);
  const companyMap = useCompanyStore(state => state.companies);
  const { colors, accentBackgroundColor } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const useSimpleList = Platform.OS !== 'web' || isXS;
  // Let the page own scrolling instead of nesting a virtualized list.
  const hasOuterScroll = Platform.OS === 'macos' || embeddedInPage;
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  const accentColor = accentBackgroundColor === 'multicolor' ? '#22C55E' : colors.accent_color;

  const styles = useMemo(() => StyleSheet.create({
    triggerButton: {
      flexDirection: 'row',
      alignItems: 'center',
      height: 34,
      paddingLeft: 10,
      paddingRight: 6,
      borderRadius: 18,
    },
    triggerButtonHovered: {
      backgroundColor: colors.solid_color_tertiary,
    },
    chevronIcon: {
      marginLeft: 6,
    },
    notice: {
      borderRadius: 8,
      padding: 12,
      backgroundColor: colors.solid_color_block,
      gap: 6,
      marginHorizontal: 0,
    },
    noticeRow: {
      flexDirection: 'row',
      gap: 8,
      alignItems: 'flex-start',
    },
    noticeIcon: {
      marginTop: 2,
    },
    searchContainer: {
      position: 'relative',
      paddingHorizontal: 12,
      paddingTop: 4,
      paddingBottom: 8,
    },
    searchInput: {
      backgroundColor: colors.solid_color_tertiary,
      padding: 10,
      paddingLeft: 36,
      paddingRight: 36,
      borderRadius: 8,
      fontSize: 14,
      color: colors.text_color_primary,
    },
    searchIcon: {
      position: 'absolute',
      left: 24,
      top: '50%',
      transform: [{ translateY: -5 }],
      zIndex: 1,
    },
    clearIcon: {
      position: 'absolute',
      right: 24,
      top: '50%',
      transform: [{ translateY: -7 }],
      zIndex: 1,
    },
    listOuter: {
      flex: hasOuterScroll ? undefined : 1,
    },
    currentBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 10,
      paddingHorizontal: 16,
      gap: 10,
      backgroundColor: colors.solid_color_tertiary,
      borderBottomWidth: 0.5,
      borderBottomColor: colors.border_color_secondary,
      marginHorizontal: 12,
      borderRadius: 8,
      marginBottom: 4,
    },
    loadingFooter: {
      paddingVertical: 16,
      alignItems: 'center',
    },
  }), [colors, hasOuterScroll]);

  const [textModelIdLocal, setTextModelIdLocal] = useState(defaultTextModelId || null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setTextModelIdLocal(defaultTextModelId || null);
  }, [defaultTextModelId]);

  // Fetch all pages of models on mount
  useEffect(() => {
    if (!isFetchedAllModels && !isLoadingAllModels) {
      fetchAllModels(1);
    }
  }, [isFetchedAllModels, isLoadingAllModels, fetchAllModels]);

  // Fetch remaining pages once first page loads
  useEffect(() => {
    if (isFetchedAllModels && modelsPagination?.hasMore && !isLoadingAllModels) {
      const nextPage = (modelsPagination.current || 1) + 1;
      fetchAllModels(nextPage);
    }
  }, [isFetchedAllModels, modelsPagination, isLoadingAllModels, fetchAllModels]);

  const enrichModel = useCallback((m) => ({
    ...m,
    companyName: (m.companyId && companyMap?.[m.companyId]?.name) || null,
  }), [companyMap]);

  const textModels = useMemo(() => {
    const sourceIds = allModelIds?.length > 0 ? allModelIds : (featuredModelIds || []);
    return sourceIds
      .map(id => allModels[id])
      .filter(isActiveModel)
      .map(enrichModel)
      .sort((a, b) => (b.orderWeight || 0) - (a.orderWeight || 0));
  }, [allModels, allModelIds, featuredModelIds, enrichModel]);

  // Debounced server-side search
  const handleSearchChange = useCallback((text) => {
    setSearchQuery(text);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (text.trim()) {
      searchTimerRef.current = setTimeout(() => {
        try { searchModels(text.trim()); } catch (_) {}
      }, 300);
    } else {
      try { searchModels(''); } catch (_) {}
    }
  }, [searchModels]);

  const searchResultModels = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return (searchResults || [])
      .filter(isActiveModel)
      .map(enrichModel)
      .sort((a, b) => (b.orderWeight || 0) - (a.orderWeight || 0));
  }, [searchResults, searchQuery, enrichModel]);

  const displayModels = searchQuery.trim() ? searchResultModels : textModels;

  const currentModelName = textModelIdLocal
    ? (allModels?.[textModelIdLocal]?.name || t('settings.personalization.selectedModel'))
    : t('settings.personalization.autoSelect');

  const Trigger = ({ modelId }) => {
    const [isHovered, setIsHovered] = useState(false);
    const label = modelId ? (allModels?.[modelId]?.name || t('settings.personalization.selectedModel')) : t('settings.personalization.autoSelect');
    return (
      <Pressable
        style={[styles.triggerButton, isHovered && styles.triggerButtonHovered]}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
      >
        <Text color="primary" size="s">{label}</Text>
        <View style={styles.chevronIcon}>
          <Icon name="chevron-down" size={10} color={colors.text_color_tertiary} />
        </View>
      </Pressable>
    );
  };

  const handleSelect = useCallback(async (modelId) => {
    const prev = textModelIdLocal;
    const newId = modelId === textModelIdLocal ? null : modelId;
    try {
      setTextModelIdLocal(newId);
      await setDefaultModel(newId, 'text');
      addToast(
        newId
          ? t('settings.personalization.defaultTextModelUpdated')
          : t('settings.personalization.defaultTextModelRemoved'),
        'success'
      );
    } catch (error) {
      setTextModelIdLocal(prev);
      if (error?.name === 'ApiError' && error?.message) {
        addToast(error.message, 'error', error?.data?.actions);
      } else {
        addToast(
          newId
            ? t('settings.personalization.failedToSetTextModel')
            : t('settings.personalization.failedToRemoveTextModel'),
          'error'
        );
      }
    }
  }, [setDefaultModel, addToast, textModelIdLocal]);

  const handleSetDefaultTextModel = useCallback(async (modelIds) => {
    const modelId = modelIds?.[0] || null;
    handleSelect(modelId === textModelIdLocal ? null : modelId || null);
  }, [handleSelect, textModelIdLocal]);

  const renderModelRow = useCallback(({ item }) => (
    <ModelRow
      model={item}
      isSelected={item.id === textModelIdLocal}
      onPress={() => handleSelect(item.id)}
      colors={colors}
      accentColor={accentColor}
    />
  ), [textModelIdLocal, handleSelect, colors, accentColor]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    searchModels('');
    searchInputRef.current?.focus();
  }, [searchModels]);

  const listHeader = useMemo(() => (
    <>
      {/* Current selection banner */}
      <View style={styles.currentBanner}>
        <Icon name="circle-check" size={16} color={accentColor} solid />
        <Text size="s" color="secondary">
          {t('settings.personalization.currentDefault')}: <Text size="s" weight="medium">{currentModelName}</Text>
        </Text>
      </View>

      {/* Search */}
      <View style={styles.searchContainer}>
        <View style={styles.searchIcon}>
          <Icon name="search" size={14} color={colors.text_color_secondary} />
        </View>
        <TextInput
          ref={searchInputRef}
          style={styles.searchInput}
          placeholder={t('settings.personalization.searchModels')}
          placeholderTextColor={colors.text_color_secondary}
          value={searchQuery}
          onChangeText={handleSearchChange}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {searchQuery.length > 0 && (
          <Pressable style={styles.clearIcon} onPress={handleClearSearch}>
            <Icon name="xmark" size={14} color={colors.text_color_secondary} />
          </Pressable>
        )}
      </View>

      {/* Auto row - only when not searching */}
      {!searchQuery.trim() && (
        <Pressable
          onPress={() => handleSelect(textModelIdLocal)}
          style={[
            nativeStyles.row,
            { borderBottomColor: colors.border_color_secondary },
          ]}
        >
          <View style={[nativeStyles.radio, { borderColor: !textModelIdLocal ? accentColor : colors.text_color_tertiary }]}>
            {!textModelIdLocal && <View style={[nativeStyles.radioDot, { backgroundColor: accentColor }]} />}
          </View>
          <View style={[nativeStyles.logoPlaceholder, { backgroundColor: colors.solid_color_tertiary }]}>
            <Icon name="wand-magic-sparkles" size={12} color={colors.text_color_tertiary} />
          </View>
          <View style={nativeStyles.info}>
            <Text size="s" weight={!textModelIdLocal ? 'medium' : 'regular'}>{t('settings.personalization.autoSelect')}</Text>
            <Text size="xs" color="tertiary">{t('settings.personalization.autoSelectDescription')}</Text>
          </View>
        </Pressable>
      )}
    </>
  ), [textModelIdLocal, handleSelect, colors, accentColor, searchQuery, currentModelName, handleClearSearch, handleSearchChange, styles]);

  const listFooter = useMemo(() => {
    if (isSearching || isLoadingAllModels) {
      return (
        <View style={styles.loadingFooter}>
          <ActivityIndicator size="small" color={colors.text_color_secondary} />
        </View>
      );
    }
    return null;
  }, [isSearching, isLoadingAllModels, colors]);

  const listEmpty = useMemo(() => {
    if (isSearching || isLoadingAllModels) return null;
    return searchQuery.trim() ? (
      <View style={{ padding: 24, alignItems: 'center' }}>
        <Text color="secondary" size="s">{t('settings.personalization.noResults')}</Text>
      </View>
    ) : null;
  }, [searchQuery, isSearching, isLoadingAllModels]);

  return (
    <View style={{ gap: 12, flex: hasOuterScroll ? undefined : 1, ...(embeddedInGeneral && { paddingBottom: 20, borderBottomWidth: 0.5, borderBottomColor: colors.border_color_secondary }) }}>
      {invitation.visible && <PersonalOnboardingInvitation compact={useSimpleList} />}
      {(!useSimpleList || embeddedInGeneral) && (
        <SettingsSection isLast title={t('settings.personalization.defaultTextModel')} subtitle={t('settings.personalization.chatModeScope')}>
          {useSimpleList ? <Button
            text={currentModelName}
            accessibilityLabel={`${t('settings.personalization.chooseDefaultModel')}: ${currentModelName}`}
            accessibilityState={{ expanded: isPickerOpen }}
            variant="secondary"
            size="small"
            appendIcon={isPickerOpen ? 'chevron-up' : 'chevron-down'}
            onPress={() => setIsPickerOpen(value => !value)}
          /> : <ModelSelectorDetailedDropdown
            TriggerComponent={Trigger}
            triggerComponentProps={{ modelId: textModelIdLocal }}
            shouldCloseOnSelect={true}
            modelIds={textModelIdLocal ? [textModelIdLocal] : []}
            onChange={handleSetDefaultTextModel}
            maxModels={1}
            outputs={["text"]}
          />}
        </SettingsSection>
      )}
      {useSimpleList && !embeddedInGeneral && <Text size="s" color="secondary">{t('settings.personalization.chatModeScope')}</Text>}
      {(!useSimpleList || !embeddedInGeneral || isPickerOpen) && <View style={styles.notice}>
        <View style={styles.noticeRow}>
          <View style={styles.noticeIcon}>
            <Icon name="circle-info" size={14} color={colors.text_color_secondary} />
          </View>
          <Text size="s" color="secondary" style={{ flex: 1 }}>
            {t('settings.personalization.intentEngineNotice')}
          </Text>
        </View>
      </View>}

      {useSimpleList && (!embeddedInGeneral || isPickerOpen) && (
        <View style={styles.listOuter}>
          {embeddedInPage ? <View>
            {listHeader}
            {displayModels.map(item => <React.Fragment key={item.id}>{renderModelRow({ item })}</React.Fragment>)}
            {!displayModels.length && listEmpty}
            {listFooter}
          </View> : <FlatList
            data={displayModels}
            renderItem={renderModelRow}
            keyExtractor={item => item.id}
            extraData={textModelIdLocal}
            estimatedItemSize={56}
            ListHeaderComponent={listHeader}
            ListEmptyComponent={listEmpty}
            ListFooterComponent={listFooter}
            showsVerticalScrollIndicator={!hasOuterScroll}
            keyboardShouldPersistTaps="handled"
            scrollEnabled={!hasOuterScroll}
          />}
        </View>
      )}
      {useSimpleList && embeddedInGeneral && isPickerOpen && <Button
        text={t('settings.personalization.closeModelPicker')}
        variant="secondary"
        size="small"
        onPress={() => setIsPickerOpen(false)}
      />}
    </View>
  );
}

export default React.memo(PersonalizationSettings);
